import elrs from "../data/elrs.json";
import utils from "./elrUtils";

/*
 * Finds the previous point of a given point, if it exists
 */
const findPreviousPoint = (segment, pointIndex) => {
  if (pointIndex === 0) {
    return null;
  }

  return segment["points"][pointIndex - 1];
};

/*
 * Finds the next point of a given point, if it exists
 */
const findNextPoint = (segment, pointIndex) => {
  if (pointIndex === segment["points"].length - 1) {
    return null;
  }

  return segment["points"][pointIndex + 1];
};

/*
 * Returns the distance of a point from the start of the ELR segment in miles
 */
const getPointMileage = (elr, sequence, lineMileageFrom) => {
  let distance = 0;
  let points;

  for (let i = 0; i < elrs.length; i++) {
    if (elrs[i].elr === elr) {
      points = elrs[i].points;
      break;
    }
  }

  for (let i = 0; i < sequence; i++) {
    // Add on distance between the current and previous point
    distance += utils.calcDistanceBetweenPointsInMiles(points[i], points[i + 1]);
  }

  // Add on distance from start of ELR segment
  distance += utils.getMilesFromMilesAndYards(lineMileageFrom);

  return distance;
};

/*
 * This function calculates:
 * distanceToTrack - the distance in miles to the nearest track location (from latLng)
 * partialDistance - the distance in miles between the nearest point and the nearest track location
 *
 * For the source of these calculations see formula under "Algebraic proof using the Pythagorean theorem"
 * on the following wikipedia page:
 * https://en.wikipedia.org/wiki/Heron%27s_formula#Proofs
 *
 * To see what distances A,B,C and D represent:
 * https://en.wikipedia.org/wiki/Heron%27s_formula#/media/File:Triangle_with_notations_3.svg
 *
 * Partial distance is 'c - d' in the above triangle diagram
 * Distance to track is 'h' in the above triangle diagram
 *
 */
const calcDistanceToTrack = (pointLatLng, adjacentPointLatLng, latLng) => {
  // Calculate the distance between each of the points
  const distanceA = utils.calcDistanceBetweenPointsInMiles(latLng, pointLatLng);
  const distanceB = utils.calcDistanceBetweenPointsInMiles(latLng, adjacentPointLatLng);
  const distanceC = utils.calcDistanceBetweenPointsInMiles(pointLatLng, adjacentPointLatLng);

  const distanceD =
    (-Math.pow(distanceA, 2) + Math.pow(distanceB, 2) + Math.pow(distanceC, 2)) / (2 * distanceC);

  const partialDistance = distanceC - distanceD;
  const distanceToTrack = Math.sqrt(Math.abs(Math.pow(distanceB, 2) - Math.pow(distanceD, 2)));

  return {
    partialDistance,
    distanceToTrack,
  };
};

/*
 * Calculates the mileage for a given lat/lng
 * Mileage is the distance along the track from the start of the ELR segment
 * to the nearest track location (from the given lat/lng)
 */
const getTrackMileage = (latLng, segment) => {
  let mileage = getPointMileage(segment["elr"], segment["nearestPoint"].sequence, segment["from"]);

  // Set distanceToTrack to our current best guess (in miles)
  let distanceToTrack = segment["nearestPoint"].distanceInMetres / 1609.344;

  if (segment["previousPoint"] && segment["nextPoint"]) {
    // latLng location is somewhere along ELR segment (not the start or end)
    // so calculate the partial distances to nearestPoint from latLng using the adjacent points
    const prevDistance = calcDistanceToTrack(
      segment["nearestPoint"].location,
      segment["previousPoint"],
      latLng
    );
    const nextDistance = calcDistanceToTrack(
      segment["nearestPoint"].location,
      segment["nextPoint"],
      latLng
    );

    if (prevDistance.distanceToTrack < nextDistance.distanceToTrack) {
      // nearest track location is between the nearestPoint and the previousPoint
      // so we want to subtract the partialDistance from our total mileage
      distanceToTrack = prevDistance.distanceToTrack;
      mileage -= prevDistance.partialDistance;
    } else {
      // nearest track location is between the nearestPoint and the nextPoint
      // so we want to add on the partialDistance to our total mileage
      distanceToTrack = nextDistance.distanceToTrack;
      mileage += nextDistance.partialDistance;
    }
  }

  return {
    mileage,
    distanceToTrack,
  };
};

/*
 * Sorts elrsInRadius into an array
 * sorted by lowest distance to furthest distance
 */
const sortELRsInRadiusByDistance = (elrsInRadius) => {
  const sorted = [];

  for (let elr in elrsInRadius) {
    sorted.push(elrsInRadius[elr]);
  }

  sorted.sort((a, b) => {
    if (a.distanceToTrackInYards < b.distanceToTrackInYards) {
      return -1;
    }
    if (a.distanceToTrackInYards > b.distanceToTrackInYards) {
      return 1;
    }
    return 0;
  });

  return sorted;
};

/*
 * Gets data for all ELRs within a given radius from a lat/lng
 */
const getELRsInRadius = (latLng, radiusInMetres) => {
  let elrsInRadius = {};

  for (let i = 0; i < elrs.length; i++) {
    const currELR = elrs[i]["elr"];

    for (let j = 0; j < elrs[i]["points"].length; j++) {
      const distanceInMetres =
        utils.calcDistanceBetweenPointsInMiles(latLng, elrs[i]["points"][j]) * 1609.344;

      if (distanceInMetres <= radiusInMetres) {
        // see if we already have found a point within radius for given ELR
        if (elrsInRadius[currELR]) {
          // see if current nearest point for ELR is further than this one
          // if so then replace it with this one
          if (elrsInRadius[currELR].nearestPoint.distanceInMetres > distanceInMetres) {
            elrsInRadius[currELR] = {
              previousPoint: findPreviousPoint(elrs[i], j),
              nearestPoint: {
                sequence: j,
                location: elrs[i]["points"][j],
                distanceInMetres,
              },
              nextPoint: findNextPoint(elrs[i], j),
              from: elrs[i]["from"],
              elr: elrs[i]["elr"],
            };
          }
        } else {
          // we've found a point within radius which has a different ELRID
          // from any other points found, so add to elrsInRadius
          elrsInRadius[currELR] = {
            previousPoint: findPreviousPoint(elrs[i], j),
            nearestPoint: {
              sequence: j,
              location: elrs[i]["points"][j],
              distanceInMetres,
            },
            nextPoint: findNextPoint(elrs[i], j),
            from: elrs[i]["from"],
            elr: elrs[i]["elr"],
          };
        }
      }
    }

    if (elrsInRadius[currELR]) {
      const { mileage, distanceToTrack } = getTrackMileage(latLng, elrsInRadius[currELR]);
      const parts = String(mileage).split(".");

      const miles = Number(parts[0]);
      const yards = parts[1] ? Number(`0.${parts[1]}`) * 1760 : 0;

      elrsInRadius[currELR].miles = miles;
      elrsInRadius[currELR].yards = yards;
      elrsInRadius[currELR].distanceToTrackInYards = distanceToTrack * 1760;
    }
  }

  return sortELRsInRadiusByDistance(elrsInRadius);
};

export default { getELRsInRadius };
